import type { ReactElement } from 'react';
import {
  buildGaugeGradientId,
  buildGradientId,
  getGradientLightColor,
  type GradientStop,
} from '../theme/chartGradients';

type ChartSvgDefsProps = Readonly<{
  chartId: string;
  colors: readonly string[];
  gaugeStops?: ReadonlyArray<Readonly<{ index: number; baseColor: string }>>;
}>;

function renderLinearGradient(
  id: string,
  baseColor: string,
  direction: 'vertical' | 'horizontal'
): ReactElement {
  const x2 = direction === 'vertical' ? '0%' : '100%';
  const y2 = direction === 'vertical' ? '100%' : '0%';

  return (
    <linearGradient key={id} id={id} x1='0%' y1='0%' x2={x2} y2={y2}>
      <stop offset='0%' stopColor={getGradientLightColor(baseColor)} />
      <stop offset='100%' stopColor={baseColor} />
    </linearGradient>
  );
}

function renderGradients(
  stops: readonly GradientStop[],
  direction: 'vertical' | 'horizontal'
): ReactElement[] {
  return stops.map(({ id, baseColor }) =>
    renderLinearGradient(id, baseColor, direction)
  );
}

export function ChartSvgDefs({
  chartId,
  colors,
  gaugeStops = [],
}: ChartSvgDefsProps): ReactElement {
  const sliceStops = colors.map((baseColor, index) => ({
    id: buildGradientId(chartId, index),
    baseColor,
  }));

  const gaugeGradientStops = gaugeStops.map(({ index, baseColor }) => ({
    id: buildGaugeGradientId(chartId, index),
    baseColor,
  }));

  if (sliceStops.length === 0 && gaugeGradientStops.length === 0) {
    return <></>;
  }

  return (
    <svg
      width={0}
      height={0}
      aria-hidden
      style={{ position: 'absolute', visibility: 'hidden' }}
    >
      <defs>
        {renderGradients(sliceStops, 'vertical')}
        {renderGradients(gaugeGradientStops, 'horizontal')}
      </defs>
    </svg>
  );
}
